import { useEffect, useMemo, useState } from "react";
import { History, Lock, MinusCircle, Plus, Trash2, User, Edit2 } from "lucide-react";
import dayjs from "dayjs";
import { Select } from "antd";

import { Button } from "../../../components/ui/button";
import { Input, Textarea } from "../../../components/ui/input";
import { DialogModal, ConfirmPopupModal } from "../../../components/ui/dialogModal";
import ToastMsg from "../../../components/common/ToastMsg";
import useApiMethods from "../../../lib/useApiMethods";
import { EMPLOYEE_DEDUCTIONS, SALARY_COMPONENT } from "../../../constants/api";
import { checkUserPermissionFunc } from "../../../components/common/CommonFunctions";
import { cn } from "../../../utils/tailwindUtils";
import { modalDescriptionFieldProps, modalFieldLabelClass, modalInputClass, modalTextareaClass } from "../../../components/ui/fieldStyles";
import { isOwnEmployeeProfile } from "../../../utils/employeeProfile";
import { usePayrollProcessLocked } from "../../../hooks/usePayrollProcessLocked";
import PayrollProcessLockNotice from "../../../components/payroll/PayrollProcessLockNotice";

const PAYROLL_MONTH_LOCKED_DEDUCTION_MESSAGE =
    "Payroll for this month is already completed. Deductions for this month cannot be added, changed or removed.";

const MONTH_OPTIONS = [
    { value: 1, label: "January" },
    { value: 2, label: "February" },
    { value: 3, label: "March" },
    { value: 4, label: "April" },
    { value: 5, label: "May" },
    { value: 6, label: "June" },
    { value: 7, label: "July" },
    { value: 8, label: "August" },
    { value: 9, label: "September" },
    { value: 10, label: "October" },
    { value: 11, label: "November" },
    { value: 12, label: "December" },
];

const EmployeeDeductionContent = ({ employeeID, readOnly = false }) => {
    const { fetch, create, update, remove } = useApiMethods();
    const viewingOwnProfile = isOwnEmployeeProfile(employeeID);
    const payrollLocked = usePayrollProcessLocked();

    const deductionPerm = checkUserPermissionFunc("employee", "employee deductions", "SubMenu");
    const [listMeta, setListMeta] = useState(null);
    const canAdd =
        !readOnly && !viewingOwnProfile && !payrollLocked && (listMeta?.can_add ?? deductionPerm.add);
    const canEdit =
        !readOnly && !viewingOwnProfile && !payrollLocked && (listMeta?.can_edit ?? deductionPerm.edit);
    const canDelete =
        !readOnly && !viewingOwnProfile && !payrollLocked && (listMeta?.can_delete ?? deductionPerm.delete);

    const now = useMemo(() => dayjs(), []);
    const [payrollMonth, setPayrollMonth] = useState(() => dayjs().month() + 1);
    const [payrollYear, setPayrollYear] = useState(() => dayjs().year());
    const [deductions, setDeductions] = useState([]);
    const [history, setHistory] = useState([]);
    const [variableComponents, setVariableComponents] = useState([]);
    const [loading, setLoading] = useState(false);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [showHistory, setShowHistory] = useState(false);
    const [dialogOpen, setDialogOpen] = useState(false);
    const [editTarget, setEditTarget] = useState(null);
    const [deleteTarget, setDeleteTarget] = useState(null);
    const [saving, setSaving] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [form, setForm] = useState({
        salary_component_id: null,
        amount: "",
        reason: "",
    });

    const yearOptions = useMemo(() => {
        const current = now.year();
        return [current - 1, current, current + 1].map((y) => ({ value: y, label: String(y) }));
    }, [now]);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await fetch(SALARY_COMPONENT, {
                    drop_down: "True",
                    variable_only: "true",
                    component_type: "deduction",
                });
                if (cancelled) return;
                if (res?.status === "success") {
                    const rows = Array.isArray(res.data)
                        ? res.data
                        : (res.data?.result || []);
                    setVariableComponents(
                        rows.filter(
                            (c) =>
                                String(c.component_type || "").toLowerCase() === "deduction" &&
                                String(c.classification || "").toLowerCase() === "variable",
                        ),
                    );
                } else {
                    setVariableComponents([]);
                }
            } catch (_) {
                if (!cancelled) setVariableComponents([]);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
        if (!employeeID) return;
        let cancelled = false;
        (async () => {
            setLoading(true);
            try {
                const res = await fetch(EMPLOYEE_DEDUCTIONS, {
                    employee_id: employeeID,
                    payroll_month: payrollMonth,
                    payroll_year: payrollYear,
                });
                if (cancelled) return;
                if (res?.status === "success") {
                    setDeductions(res.data || []);
                    setListMeta(res.meta || null);
                } else {
                    ToastMsg("error", res?.message || "Failed to load deductions");
                    setListMeta(null);
                }
            } catch (error) {
                if (!cancelled) ToastMsg("error", error.message);
            } finally {
                if (!cancelled) setLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID, payrollMonth, payrollYear]);

    useEffect(() => {
        if (!employeeID || !showHistory) {
            setHistory([]);
            return;
        }
        let cancelled = false;
        (async () => {
            setHistoryLoading(true);
            try {
                const res = await fetch(EMPLOYEE_DEDUCTIONS, {
                    employee_id: employeeID,
                    view: "history",
                    payroll_month: payrollMonth,
                    payroll_year: payrollYear,
                });
                if (cancelled) return;
                if (res?.status === "success") {
                    setHistory(res.data || []);
                } else {
                    ToastMsg("error", res?.message || "Failed to load deduction history");
                    setHistory([]);
                }
            } catch (error) {
                if (!cancelled) {
                    ToastMsg("error", error.message);
                    setHistory([]);
                }
            } finally {
                if (!cancelled) setHistoryLoading(false);
            }
        })();
        return () => { cancelled = true; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [employeeID, payrollMonth, payrollYear, showHistory]);

    const reloadDeductions = async () => {
        if (!employeeID) return;
        setLoading(true);
        try {
            const res = await fetch(EMPLOYEE_DEDUCTIONS, {
                employee_id: employeeID,
                payroll_month: payrollMonth,
                payroll_year: payrollYear,
            });
            if (res?.status === "success") {
                setDeductions(res.data || []);
                setListMeta(res.meta || null);
            }
        } finally {
            setLoading(false);
        }
    };

    const reloadHistory = async () => {
        if (!employeeID || !showHistory) return;
        setHistoryLoading(true);
        try {
            const res = await fetch(EMPLOYEE_DEDUCTIONS, {
                employee_id: employeeID,
                view: "history",
                payroll_month: payrollMonth,
                payroll_year: payrollYear,
            });
            if (res?.status === "success") {
                setHistory(res.data || []);
            }
        } finally {
            setHistoryLoading(false);
        }
    };

    const resetForm = () => {
        setForm({ salary_component_id: null, amount: "", reason: "" });
        setEditTarget(null);
        setDialogOpen(false);
    };

    const openAddDialog = () => {
        setEditTarget(null);
        setForm({ salary_component_id: null, amount: "", reason: "" });
        setDialogOpen(true);
    };

    const openEditDialog = (row) => {
        setEditTarget(row);
        setForm({
            salary_component_id: row.salary_component,
            amount: row.amount || "",
            reason: row.reason || "",
        });
        setDialogOpen(true);
    };

    const handleSave = async () => {
        if (editTarget && !canEdit) return;
        if (!editTarget && !canAdd) return;
        const amount = parseFloat(form.amount);
        if (!editTarget && !form.salary_component_id) {
            ToastMsg("error", "Select a deduction component");
            return;
        }
        if (!amount || amount <= 0) {
            ToastMsg("error", "Enter a valid amount greater than zero");
            return;
        }

        setSaving(true);
        try {
            let res;
            if (editTarget) {
                res = await update(EMPLOYEE_DEDUCTIONS, {
                    id: editTarget.id,
                    amount,
                    reason: form.reason.trim(),
                });
            } else {
                res = await create(EMPLOYEE_DEDUCTIONS, {
                    employee_id: employeeID,
                    salary_component_id: form.salary_component_id,
                    payroll_month: payrollMonth,
                    payroll_year: payrollYear,
                    amount,
                    reason: form.reason.trim(),
                });
            }
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Saved");
                resetForm();
                await reloadDeductions();
                if (showHistory) await reloadHistory();
            } else {
                ToastMsg("error", res?.message || "Failed to save deduction");
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setSaving(false);
        }
    };

    const handleDelete = async () => {
        if (!deleteTarget?.id) return;
        setDeleting(true);
        try {
            const res = await remove(`${EMPLOYEE_DEDUCTIONS}?id=${deleteTarget.id}`);
            if (res?.status === "success") {
                ToastMsg("success", res.message || "Deduction removed");
                setDeleteTarget(null);
                await reloadDeductions();
                if (showHistory) await reloadHistory();
            } else {
                ToastMsg("error", res?.message || "Failed to remove deduction");
            }
        } catch (error) {
            ToastMsg("error", error.message);
        } finally {
            setDeleting(false);
        }
    };

    return (
        <div className="space-y-4">
            <PayrollProcessLockNotice />
            {listMeta?.payroll_month_locked && (
                <div
                    className="rounded-xl border border-amber-300/80 bg-amber-50 dark:bg-amber-950/30 dark:border-amber-800/60 px-4 py-3 flex items-start gap-3"
                    role="status"
                >
                    <span className="mt-0.5 flex h-8 w-8 shrink-0 items-center justify-center rounded-lg bg-amber-200/80 text-amber-800 dark:bg-amber-900/50 dark:text-amber-200">
                        <Lock className="h-4 w-4" />
                    </span>
                    <p className="text-sm text-gray-800 dark:text-gray-200">{PAYROLL_MONTH_LOCKED_DEDUCTION_MESSAGE}</p>
                </div>
            )}
            <div className="flex flex-wrap items-start justify-between gap-3">
                <div>
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-gray-100 flex items-center gap-2">
                        <MinusCircle className="h-5 w-5 text-rose-600" />
                        Variable Deductions
                    </h3>
                    <p className="text-sm text-muted-foreground mt-1">
                        Monthly variable deductions and fines assigned separately from fixed salary structure.
                    </p>
                </div>
                <div className="flex flex-wrap items-center gap-2">
                    <Select
                        value={payrollMonth}
                        onChange={setPayrollMonth}
                        options={MONTH_OPTIONS}
                        className="min-w-[130px]"
                        size="large"
                    />
                    <Select
                        value={payrollYear}
                        onChange={setPayrollYear}
                        options={yearOptions}
                        className="min-w-[100px]"
                        size="large"
                    />
                    <Button
                        variant="outline"
                        onClick={() => setShowHistory((v) => !v)}
                        className="gap-2"
                    >
                        <History className="h-4 w-4" />
                        {showHistory ? "Hide History" : "Show History"}
                    </Button>
                    {canAdd && (
                        <Button onClick={openAddDialog} className=" bg-sidebar text-white hover:bg-sidebar-primary gap-2">
                            <Plus className="h-4 w-4" />
                            Assign Deduction
                        </Button>
                    )}
                </div>
            </div>

            {loading ? (
                <div className="flex items-center justify-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sidebar-primary" />
                </div>
            ) : deductions.length === 0 ? (
                <div className="rounded-lg border border-dashed border-gray-300 dark:border-gray-600 p-8 text-center">
                    <MinusCircle className="h-10 w-10 mx-auto text-gray-400 mb-3" />
                    <p className="text-sm text-muted-foreground">
                        No variable deductions for {MONTH_OPTIONS.find((m) => m.value === payrollMonth)?.label} {payrollYear}.
                    </p>
                    {canAdd && (
                        <Button variant="outline" className="mt-4 gap-2" onClick={openAddDialog}>
                            <Plus className="h-4 w-4" />
                            Assign first deduction
                        </Button>
                    )}
                </div>
            ) : (
                <div className="space-y-3">
                    {deductions.map((item) => (
                        <div
                            key={item.id}
                            className="rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50/50 dark:bg-gray-900/30 p-4"
                        >
                            <div className="flex items-start justify-between gap-3">
                                <div>
                                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                                        {item.component_name || "Deduction"}
                                    </p>
                                    <p className="text-lg font-semibold text-rose-600 mt-1">
                                        ₹{item.amount}
                                    </p>
                                    {item.reason && (
                                        <p className="text-sm text-muted-foreground mt-1">{item.reason}</p>
                                    )}
                                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs text-muted-foreground">
                                        <span className="inline-flex items-center gap-1">
                                            <User className="h-3.5 w-3.5" />
                                            {item.assigned_by_name || item.created_by || "Unknown"}
                                        </span>
                                        <span>
                                            {item.created_date
                                                ? dayjs(item.created_date).format("DD-MM-YYYY hh:mm A")
                                                : "-"}
                                        </span>
                                    </div>
                                </div>
                                {(canEdit || canDelete) && (
                                    <div className="flex gap-1 shrink-0">
                                        {canEdit && (
                                            <Button variant="ghost" size="sm" onClick={() => openEditDialog(item)}>
                                                <Edit2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                        {canDelete && (
                                            <Button
                                                variant="ghost"
                                                size="sm"
                                                className="text-red-600 hover:text-red-700"
                                                onClick={() => setDeleteTarget(item)}
                                            >
                                                <Trash2 className="h-4 w-4" />
                                            </Button>
                                        )}
                                    </div>
                                )}
                            </div>
                        </div>
                    ))}
                </div>
            )}

            {showHistory && (
                <div className="space-y-3 pt-4 border-t border-gray-200 dark:border-gray-700">
                    <h4 className="text-sm font-semibold text-gray-800 dark:text-gray-200 flex items-center gap-2">
                        <History className="h-4 w-4" />
                        Deduction History
                        <span className="text-xs font-normal text-muted-foreground">
                            ({MONTH_OPTIONS.find((m) => m.value === payrollMonth)?.label} {payrollYear})
                        </span>
                    </h4>
                    {historyLoading ? (
                        <div className="flex items-center justify-center h-20">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-sidebar-primary" />
                        </div>
                    ) : history.length === 0 ? (
                        <p className="text-sm text-muted-foreground">No history recorded for this period.</p>
                    ) : (
                        history.map((item) => (
                            <div
                                key={item.id}
                                className={cn(
                                    "rounded-md border border-gray-200 dark:border-gray-700 px-3 py-2 text-sm",
                                    item.reason?.toLowerCase().includes("payroll") && "border-amber-200 bg-amber-50/50 dark:border-amber-800 dark:bg-amber-900/10",
                                )}
                            >
                                <div className="flex flex-wrap justify-between gap-2">
                                    <span className="font-medium">{item.action_label} — {item.component_name}</span>
                                    <span className="text-xs text-muted-foreground">
                                        {item.created_date
                                            ? dayjs(item.created_date).format("DD-MM-YYYY hh:mm A")
                                            : "-"}
                                    </span>
                                </div>
                                <p className="text-muted-foreground mt-1">
                                    {item.action === "updated"
                                        ? `₹${item.old_amount || "-"} → ₹${item.new_amount || "-"}`
                                        : item.action === "removed"
                                            ? `Removed ₹${item.old_amount || "-"}`
                                            : `₹${item.new_amount || "-"}`}
                                    {" · "}
                                    {MONTH_OPTIONS.find((m) => m.value === item.payroll_month)?.label || item.payroll_month}
                                    /{item.payroll_year}
                                </p>
                                {item.reason && (
                                    <p className="text-xs text-amber-700 dark:text-amber-300 mt-1">{item.reason}</p>
                                )}
                                <p className="text-xs text-muted-foreground mt-1">
                                    By {item.changed_by_name || item.created_by || "Unknown"}
                                </p>
                            </div>
                        ))
                    )}
                </div>
            )}

            <DialogModal
                open={dialogOpen}
                onClose={resetForm}
                onSave={handleSave}
                title={editTarget ? "Update Deduction" : "Assign Deduction"}
                saveLabel={editTarget ? "Update" : "Assign"}
                loading={saving}
                width="480px"
            >
                <div className="space-y-4">
                    {!editTarget && (
                        <div className="space-y-2">
                            <label className="text-sm font-medium">Deduction Component</label>
                            <Select
                                value={form.salary_component_id}
                                onChange={(v) => setForm((f) => ({ ...f, salary_component_id: v }))}
                                options={variableComponents.map((c) => ({
                                    value: c.id,
                                    label: c.salary_component_name,
                                }))}
                                placeholder="Select variable deduction"
                                className="w-full"
                                size="large"
                                showSearch
                                optionFilterProp="label"
                            />
                            {variableComponents.length === 0 && (
                                <p className="text-xs text-amber-600">
                                    No variable deduction components configured. Create one under Salary Component with type Deduction and classification Variable.
                                </p>
                            )}
                        </div>
                    )}
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Amount (₹)</label>
                        <Input
                            type="number"
                            min="1"
                            step="0.01"
                            value={form.amount}
                            onChange={(e) => setForm((f) => ({ ...f, amount: e.target.value }))}
                            placeholder="Enter amount"
                        />
                    </div>
                    <div className="space-y-2">
                        <label className="text-sm font-medium">Reason / Notes</label>
                        <Textarea
                            value={form.reason}
                            onChange={(e) => setForm((f) => ({ ...f, reason: e.target.value }))}
                            rows={3}
                            placeholder="e.g. Uniform recovery, ID card loss, canteen dues"
                        />
                    </div>
                </div>
            </DialogModal>

            <ConfirmPopupModal
                open={!!deleteTarget}
                onClose={() => setDeleteTarget(null)}
                onConfirm={handleDelete}
                title="Remove Deduction"
                description="This deduction will be removed for the selected month. The change will be recorded in history."
                confirmLabel="Remove"
                type="danger"
                destructive
                loading={deleting}
            />
        </div>
    );
};

export default EmployeeDeductionContent;
